import { createEventHook } from '@vueuse/core';
import { reactive } from 'vue';
import type { Bounds } from '@kitware/vtk.js/types';
import type { View } from '@/src/core/vtk/types';
import type { Maybe } from '@/src/types';

export type CameraOpKind = 'setRotation' | 'flipHorizontal' | 'flipVertical';

export interface CameraOpEvent {
  viewId: string;
  op: CameraOpKind;
  /** Absolute in-plane rotation in degrees (0–360) for setRotation. */
  angle?: number;
}

const cameraOpHook = createEventHook<CameraOpEvent>();

/** Per-view in-plane rotation tracked for the toolbar slider (degrees). */
const viewRotations = reactive<Record<string, number>>({});

/** Reactive per-view rotation map (for UI sync). */
export const viewRotationDegrees = viewRotations;

/** Viewers subscribe to apply rotate/flip requests targeted at their view. */
export const onCameraOp = cameraOpHook.on;

/** Toolbar (or shortcuts) request a camera operation on a specific view. */
export function triggerCameraOp(event: CameraOpEvent) {
  cameraOpHook.trigger(event);
}

export function getViewRotation(viewId: string): number {
  return viewRotations[viewId] ?? 0;
}

export function normalizeRotationAngle(angle: number): number {
  const normalized = angle % 360;
  return normalized < 0 ? normalized + 360 : normalized;
}

function setViewRotation(viewId: string, angle: number) {
  viewRotations[viewId] = normalizeRotationAngle(angle);
}

/** Clear stored rotation when the view camera is reset. */
export function clearViewRotation(viewId: string) {
  delete viewRotations[viewId];
}

/**
 * Apply a rotate/flip camera operation to a 2D view.
 *
 * - setRotation: rolls the camera to the requested absolute angle (clockwise
 *   on screen, matching the previous +90° button behavior).
 * - flipHorizontal: mirrors the image by viewing the slice plane from the
 *   opposite side (keeps the up direction).
 * - flipVertical: inverts the view-up vector to mirror top/bottom.
 *
 * Orientation labels and annotations follow automatically since both are
 * derived from the camera each frame.
 */
export function applyCameraOp(
  view: View,
  viewId: string,
  op: CameraOpKind,
  worldBounds: Maybe<Bounds>,
  angle?: number
) {
  const camera = view.renderer.getActiveCamera();

  if (op === 'setRotation' && angle != null) {
    const target = normalizeRotationAngle(angle);
    const current = getViewRotation(viewId);
    const delta = target - current;
    if (delta !== 0) {
      camera.roll(-delta);
      setViewRotation(viewId, target);
    }
  } else if (op === 'flipHorizontal') {
    const position = camera.getPosition();
    const focalPoint = camera.getFocalPoint();
    camera.setPosition(
      2 * focalPoint[0] - position[0],
      2 * focalPoint[1] - position[1],
      2 * focalPoint[2] - position[2]
    );
  } else if (op === 'flipVertical') {
    const viewUp = camera.getViewUp();
    camera.setViewUp(-viewUp[0], -viewUp[1], -viewUp[2]);
  }

  if (worldBounds) {
    view.renderer.resetCameraClippingRange(worldBounds);
  } else {
    view.renderer.resetCameraClippingRange();
  }
  view.requestRender();
}
